<script setup lang="ts">
/**
 * NoticeCard — ex-*(예외·안내) 페이지 공통 카드 이관(skeleton.css .auth-card.notice-card).
 * 결제 결과·메일 인증·인증 필요·이메일 충돌 등 상태 안내에 재사용.
 * - tone(.nc-ic ok/warn/danger/info): 아이콘 원형 배경·색.
 * - 아이콘은 #icon 슬롯(SVG/컴포넌트), 제목·본문 props, 액션은 #actions 슬롯.
 * 중앙 정렬 레이아웃(.layout-centered)은 BlankLayout/페이지가 담당 — 카드만 책임.
 */

interface Props {
  /** 상태 톤 — 아이콘 원형 배경/색. */
  tone?: 'info' | 'ok' | 'warn' | 'danger';
  /** 제목. */
  title: string;
  /** 본문 안내(선택). */
  message?: string;
}

withDefaults(defineProps<Props>(), {
  tone: 'info',
  message: '',
});

/** .nc-ic 톤 클래스 매핑 — skeleton.css 상태별 배경/색. */
const toneClass: Record<NonNullable<Props['tone']>, string> = {
  info: 'bg-info-soft text-brand-muted',
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
};
</script>

<template>
  <div
    class="mx-auto w-full max-w-[440px] rounded-modal border border-line bg-white p-[36px_34px] text-center shadow-md"
  >
    <!-- .nc-ic — 아이콘 원형(상태 톤) -->
    <div
      class="mx-auto mb-[18px] flex h-16 w-16 items-center justify-center rounded-full [&_svg]:h-8 [&_svg]:w-8"
      :class="toneClass[tone]"
    >
      <slot name="icon" />
    </div>

    <!-- 제목 -->
    <h1 class="m-0 font-serif text-[23px] font-extrabold tracking-[-0.02em] text-brand">
      {{ title }}
    </h1>

    <!-- 본문 -->
    <p v-if="message" class="mx-0 mb-0 mt-[9px] text-[13.5px] leading-[1.6] text-body">
      {{ message }}
    </p>

    <!-- 본문 슬롯(추가 안내 — 리스트·강조 등) -->
    <div v-if="$slots.default" class="mt-3 text-[13.5px] leading-[1.6] text-body">
      <slot />
    </div>

    <!-- 액션(버튼 등) -->
    <div v-if="$slots.actions" class="mt-6 flex flex-col gap-[10px]">
      <slot name="actions" />
    </div>
  </div>
</template>
